import type { ApolloCache, FetchResult, NormalizedCacheObject, Reference } from '@apollo/client'
// eslint-disable-next-line no-restricted-imports
import type { Modifier, ReadFieldFunction } from '@apollo/client/cache/core/types/common'
import type { DocumentNode } from 'graphql'

import type { TShipmentMember } from '../types/shipment'

/* Function to update the cache with new data
 ** This function is used to update the cache with new data after a mutation has been performed
 */
export const updateCacheFieldWithNewData = (params: {
  /**
   * The name of the field to update in the cache (e.g. shipmentMembers)
   */
  cacheFieldKey: string
  /**
   * The ID of the entity to update in the cache (e.g. shipmentID)
   */
  entityID: string | undefined
  /**
   * The name of the type (e.g. Shipment, User, etc.)
   */
  entityTypeName: string
  /**
   * The fragment to use to update the cache
   *
   * @example
   * ```ts
   * const shipmentMemberFragment = gql`
   *  fragment ShipmentMemberFragment on ShipmentMember {
   *   id
   * }
   * `
   * ```
   */
  fragment: DocumentNode
  /**
   * The name of the mutation (e.g. addShipmentMember, removeShipmentMember)
   */
  mutationResponseKey: string
}) => {
  const { cacheFieldKey, fragment, entityID, mutationResponseKey, entityTypeName } = params

  return <T>(
    cache: ApolloCache<NormalizedCacheObject>,
    mutationResult: FetchResult<{ [key: string]: T }, Record<string, T>, Record<string, T>>
  ) => {
    if (!entityID || !mutationResult.data) return

    // Read the existing data from the cache
    const existingCachedEntity = cache.readFragment<{ [key: string]: Array<T> }>({
      fragment,
      id: `${entityTypeName}:${entityID}`
    })

    // If the data is not in the cache, return
    if (!existingCachedEntity) {
      return
    }

    // Add the new item to the existing items
    const newListItem = mutationResult.data[mutationResponseKey]
    const updatedCachedEntity = {
      ...existingCachedEntity,
      [cacheFieldKey]: [...(existingCachedEntity[cacheFieldKey] || []), newListItem]
    }

    // Write the updated data back to the cache
    cache.writeFragment({
      data: updatedCachedEntity,
      fragment,
      id: `${entityTypeName}:${entityID}`
    })
  }
}

export const removeArrayItemFromCache = (params: {
  /**
   * The name of the field to update in the cache (e.g. shipmentMembers)
   */
  fieldName: string
  /**
   * The ID of the entity to update in the cache (e.g. shipmentID)
   */
  id: string | undefined
  /**
   * The name of the mutation (e.g. addShipmentMember, removeShipmentMember)
   */
  mutationReturnValueFieldName: string
  /**
   * The name of the type (e.g. Shipment, User, etc.)
   */
  typename: string
}) => {
  const { id, fieldName, mutationReturnValueFieldName, typename } = params

  return <T>(
    cache: ApolloCache<string>,
    mutationResult: FetchResult<
      { [key: string]: TShipmentMember | { id: string } },
      Record<string, T>,
      Record<string, T>
    >
  ) => {
    const cacheID = cache.identify({
      __typename: typename,
      id: id
    })
    //set the removed item to the value returned by the mutation
    const removedItem = mutationResult.data?.[mutationReturnValueFieldName as keyof typeof mutationResult.data]

    if (!removedItem) throw new Error('Did not receive removed item from mutation result')

    type CacheValue = Reference | readonly (Reference | undefined)[]
    const modifier: Modifier<CacheValue> = (
      existingItemsRefs: CacheValue = [],
      { readField }: { readField: (fieldReference: string, ref: Reference) => { id: string } }
    ) => {
      if (!Array.isArray(existingItemsRefs)) {
        return existingItemsRefs
      }

      // Determine if removedItem is of type TShipmentMember or {id: string}
      const isShipmentMemberType = 'user' in removedItem

      //set the id of the removed item
      const removedItemID = isShipmentMemberType ? removedItem?.user?.id : removedItem?.id

      //set the workspace id of the removed item
      const removedWorkspaceID = isShipmentMemberType ? removedItem?.workspace?.id : undefined

      // Filter out the item that has been removed
      return existingItemsRefs.filter((ref: Reference) => {
        // set the userID to the appropriate value depending on the type of removedItem
        const itemID = isShipmentMemberType ? readField('user', ref)?.id : readField('id', ref)

        //set the workspace id
        const workspaceID = readField('workspace', ref)?.id

        if (removedWorkspaceID && workspaceID) {
          // Remove only the specific item with the matching ID, not all items with the same workspace ID
          return !(itemID === removedItemID && workspaceID === removedWorkspaceID)
        } else {
          // If removedWorkspaceID or workspace doesn't exist, only check item id
          return itemID !== removedItemID
        }
      })
    }

    cache.modify({
      fields: {
        [fieldName]: modifier as Modifier<unknown>
      },
      id: cacheID,
      optimistic: true
    })
  }
}

export const addArrayItemToCache = (params: {
  /**
   * The name of the field to update in the cache (e.g. shipmentMembers)
   */
  fieldName: string
  /**
   * The fragment to use to update the cache
   *
   * @example
   * ```ts
   * const shipmentMemberFragment = gql`
   *  fragment ShipmentMemberFragment on ShipmentMember {
   *   id
   * }
   * `
   * ```
   */
  fragment: DocumentNode
  /**
   * The ID of the entity to update in the cache (e.g. shipmentID)
   */
  id: string | undefined
  /**
   * The name of the mutation (e.g. addShipmentMember, removeShipmentMember)
   */
  mutationReturnValueFieldName: string
  /**
   * The name of the type (e.g. Shipment, User, etc.)
   */
  typename: string
}) => {
  const { id, fragment, fieldName, mutationReturnValueFieldName, typename } = params

  return <T>(
    cache: ApolloCache<string>,
    mutationResult: FetchResult<{ [key: string]: T }, Record<string, T>, Record<string, T>>
  ) => {
    try {
      const cacheID = cache.identify({
        __typename: typename,
        id: id
      })

      const newItemData = mutationResult.data?.[mutationReturnValueFieldName as keyof typeof mutationResult.data]

      type CacheValue = Reference | readonly (Reference | undefined)[]
      const modifier: Modifier<CacheValue> = (
        existingItemsRefs: CacheValue = [],
        { readField }: { readField: ReadFieldFunction }
      ) => {
        if (!Array.isArray(existingItemsRefs)) {
          return existingItemsRefs
        }

        const newItemRef = cache.writeFragment({
          data: newItemData,
          fragment: fragment
        })

        function isObject(data: unknown): data is object {
          return typeof data === 'object' && data !== null
        }

        const isShipmentMemberType = isObject(newItemData) && 'user' in newItemData

        // Check if the new item is already in the list
        const itemAlreadyExists = existingItemsRefs.some((ref: Reference) => {
          const existingItem = readField(isShipmentMemberType ? 'user' : 'id', ref) as Reference
          const newItem = readField(isShipmentMemberType ? 'user' : 'id', newItemRef) as Reference
          if (existingItem && newItem) {
            const existingItemID = readField('id', existingItem)
            const newItemID = readField('id', newItem)
            return isShipmentMemberType ? existingItemID === newItemID : existingItem === newItem
          }

          return false
        })

        if (itemAlreadyExists) {
          return existingItemsRefs
        }

        // If not, add the new item to the list
        return [...existingItemsRefs, newItemRef]
      }

      cache.modify({
        fields: {
          [fieldName]: modifier as Modifier<unknown>
        },
        id: cacheID,
        optimistic: true
      })
    } catch (err) {
      console.log(err)
    }
  }
}
